import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';

export interface ChatSuggestionsProps {
  items: string[];
  onPick: (item: string) => void;
  disabled?: boolean;
  /**
   * `chips` — small pills, for follow-ups under an answer.
   * `list` — full-width rows in two columns with room for longer questions,
   * for questions to start with where there is space, e.g. a home page.
   */
  variant?: 'chips' | 'list';
}

/** Questions to ask next — one click to ask, or to put in the box. */
export function ChatSuggestions({ items, onPick, disabled, variant = 'chips' }: ChatSuggestionsProps) {
  if (items.length === 0) return null;

  if (variant === 'list') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {items.map((item) => (
          <ButtonBase
            key={item}
            disabled={disabled}
            onClick={() => onPick(item)}
            sx={(t) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 2,
              py: 1.5,
              textAlign: 'left',
              borderRadius: `${t.ds.radius.md}px`,
              border: `1px solid ${t.tokens.border.subtle}`,
              bgcolor: t.tokens.surface.raised,
              color: t.tokens.text.primary,
              transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              '& svg': {
                color: t.tokens.text.tertiary,
                transition: `transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              },
              '&:hover': {
                borderColor: t.tokens.border.strong,
                '& svg': { color: t.tokens.text.accent, transform: 'translateX(2px)' },
              },
              '@media (prefers-reduced-motion: reduce)': { '&:hover svg': { transform: 'none' } },
              '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
              '&.Mui-disabled': { color: t.tokens.text.disabled },
            })}
          >
            <Typography
              variant="body2"
              component="span"
              sx={{
                color: 'inherit',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item}
            </Typography>
            <ArrowForwardRounded fontSize="small" aria-hidden />
          </ButtonBase>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {items.map((item) => (
        <ButtonBase
          key={item}
          disabled={disabled}
          onClick={() => onPick(item)}
          sx={(t) => ({
            px: 1.5,
            py: 0.5,
            borderRadius: `${t.ds.radius.full}px`,
            border: `1px solid ${t.tokens.border.default}`,
            bgcolor: t.tokens.surface.raised,
            color: t.tokens.text.primary,
            '&:hover': { borderColor: t.tokens.brand.primarySoft, bgcolor: t.tokens.brand.primarySofter, color: t.tokens.text.accent },
            '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
            '&.Mui-disabled': { color: t.tokens.text.disabled },
          })}
        >
          <Typography variant="caption" sx={{ color: 'inherit' }}>{item}</Typography>
        </ButtonBase>
      ))}
    </Box>
  );
}
